import { useEffect, useRef, useState } from "react";
import { Volume2, VolumeX } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";

// Fados oficiais da Audio Experience:
// 1. "Variações em Si Menor" · 2. "Variações em Dó Maior" — Paulo Parreira
const TRACKS = ["/audio/variacoes-si-m.mp3", "/audio/variacoes-do.mp3"];
const STATE_KEY = "fam_audio_state"; // "playing" | "paused" | "done"

export const AudioExperience = () => {
    const { t } = useLanguage();
    const [playing, setPlaying] = useState(false);
    const audioRef = useRef(null);
    const trackRef = useRef(0);

    useEffect(() => {
        const audio = new Audio();
        audio.preload = "auto";
        audio.src = TRACKS[0];
        audioRef.current = audio;
        window.__famAudio = audio; // probe para QA

        const onEnded = () => {
            if (trackRef.current < TRACKS.length - 1) {
                trackRef.current += 1;
                audio.src = TRACKS[trackRef.current];
                audio.play().catch(() => setPlaying(false));
            } else {
                setPlaying(false);
                sessionStorage.setItem(STATE_KEY, "done");
            }
        };
        audio.addEventListener("ended", onEnded);

        const state = sessionStorage.getItem(STATE_KEY);
        const events = ["pointerdown", "keydown", "wheel", "touchstart"];
        let listenersActive = false;

        const disarm = () => {
            if (!listenersActive) return;
            events.forEach((e) => window.removeEventListener(e, onFirst));
            listenersActive = false;
        };

        const onSuccess = () => {
            setPlaying(true);
            sessionStorage.setItem(STATE_KEY, "playing");
            disarm();
        };

        // Listeners registados IMEDIATAMENTE (sem {once}): só desarmam após play com sucesso
        // ou se o utilizador pausar — rejeições mantêm-nos armados (evita race condition).
        const onFirst = () => {
            if (sessionStorage.getItem(STATE_KEY) === "paused") {
                disarm();
                return;
            }
            audio.play().then(onSuccess).catch(() => {});
        };

        if (state !== "paused" && state !== "done") {
            listenersActive = true;
            events.forEach((e) => window.addEventListener(e, onFirst));
            audio.play().then(onSuccess).catch(() => {});
        }

        return () => {
            audio.pause();
            audio.removeEventListener("ended", onEnded);
            disarm();
            audioRef.current = null;
            window.__famAudio = null;
        };
    }, []);

    const toggle = () => {
        const audio = audioRef.current;
        if (!audio) return;
        if (playing) {
            audio.pause();
            setPlaying(false);
            sessionStorage.setItem(STATE_KEY, "paused");
        } else {
            if (sessionStorage.getItem(STATE_KEY) === "done") {
                trackRef.current = 0;
                audio.src = TRACKS[0];
            }
            audio.play().then(() => {
                setPlaying(true);
                sessionStorage.setItem(STATE_KEY, "playing");
            }).catch(() => {});
        }
    };

    return (
        <button
            type="button"
            onClick={toggle}
            data-testid="sound-toggle"
            aria-label={playing ? t.audio.pausar : t.audio.tocar}
            aria-pressed={playing}
            title={playing ? t.audio.pausar : t.audio.tocar}
            className="fixed bottom-5 right-5 z-40 flex items-center justify-center w-11 h-11 rounded-full border border-[#c9a24b]/60 bg-[#070503]/80 backdrop-blur-sm text-[#c9a24b] hover:bg-[#c9a24b] hover:text-[#070503] transition-colors duration-300"
        >
            {playing ? <Volume2 size={18} strokeWidth={1.5} /> : <VolumeX size={18} strokeWidth={1.5} />}
        </button>
    );
};
